import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Lock, Search, X, AlertCircle } from 'lucide-react';
import Modal from './Modal';
import { ApiService } from '../services/api';
import { getErrorMessage } from '../services/supabaseClient';
import { useSubscription } from '../context/SubscriptionContext';
import { Ticket, Vehicle } from '../types';
import { validateTicketForm } from '../utils/validation';
import { logger } from '../services/logger';

type FormState = {
  pcn_number: string;
  vrn: string;
  issue_date: string; // YYYY-MM-DD
  contravention_date: string; // YYYY-MM-DD
  location: string;
  issuing_authority: string;
  amount: string; // keep as string for input
  discount_amount: string;
  stage: string;
  contravention_type: string;
};

type VehicleDetails = {
  make?: string;
  model?: string;
  colour?: string;
  yearOfManufacture?: number;
  fuelType?: string;
  taxStatus?: string;
  taxDueDate?: string | null;
  motStatus?: string;
  motExpiryDate?: string | null;
  monthOfFirstRegistration?: string;
  engineCapacity?: number;
  co2Emissions?: number;
  euroStatus?: string;
  wheelplan?: string;
  revenueWeight?: number;
  markedForExport?: boolean;
  dateOfLastV5CIssued?: string;
};

const TYPES = ['Parking Violation', 'Bus Lane', 'Moving Traffic', 'Congestion Charge', 'Low Emission Zone'] as const;

const normalizeVrm = (raw: string) => raw.replace(/\s+/g, '').toUpperCase();
const isValidVrm = (vrm: string) => /^[A-Z0-9]{5,8}$/.test(normalizeVrm(vrm));

const toIsoDateTime = (yyyyMmDd: string) => {
  // JS treats YYYY-MM-DD as UTC midnight
  const d = new Date(yyyyMmDd);
  return d.toISOString();
};

const formatMaybeDate = (raw?: string | null) => {
  if (!raw) return '-';
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function ManualTicketModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (ticket: Ticket) => void;
  onViewTicket?: (ticketId: string) => void;
}) {
  const { isOpen, onClose, onCreated, onViewTicket } = props;
  const { checkAccess, promptUpgrade } = useSubscription();
  const hasDvlaLookup = checkAccess('dvlaLookup');

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const [vehicleDetails, setVehicleDetails] = useState<VehicleDetails>({});
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupSuccess, setLookupSuccess] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdTicket, setCreatedTicket] = useState<Ticket | null>(null);
  const [createAction, setCreateAction] = useState<'created' | 'merged' | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState<FormState>(() => ({
    pcn_number: '',
    vrn: '',
    issue_date: '',
    contravention_date: '',
    location: '',
    issuing_authority: '',
    amount: '',
    discount_amount: '',
    stage: 'Notice to Owner',
    contravention_type: 'Parking Violation'
  }));

  useEffect(() => {
    if (!isOpen) return;
    setSubmitError(null);
    setLookupError(null);
    setLookupSuccess(null);
    setCreatedTicket(null);
    setCreateAction(null);
    setFieldErrors({});
    setVehicleDetails({});
    setLoadingVehicles(true);
    ApiService.getVehicles()
      .then((v) => setVehicles(v || []))
      .catch(() => setVehicles([]))
      .finally(() => setLoadingVehicles(false));
  }, [isOpen]);

  const vrmNormalized = useMemo(() => normalizeVrm(form.vrn), [form.vrn]);

  const matchingVehicles = useMemo(() => {
    const q = vrmNormalized;
    if (!q) return [];
    return vehicles
      .filter((v) => normalizeVrm(v.vrm).includes(q))
      .slice(0, 6);
  }, [vehicles, vrmNormalized]);

  const setField = (key: keyof FormState, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const applyVehicleSuggestion = (v: Vehicle) => {
    setField('vrn', v.vrm);
    setVehicleDetails({ make: v.make || undefined, model: v.model || undefined, colour: v.colour || undefined });
  };

  const runDvlaLookup = async () => {
    if (!hasDvlaLookup) return promptUpgrade();
    setLookupError(null);
    setLookupSuccess(null);
    setLookupLoading(true);
    try {
      if (!isValidVrm(form.vrn)) throw new Error('Enter a valid VRM before lookup.');
      const details = await ApiService.lookupVehicle(vrmNormalized);
      if (!details) throw new Error('No vehicle details returned.');
      setVehicleDetails(details);

      // Build comprehensive success message
      const makeModel = [details.make, details.model].filter(Boolean).join(' ').trim();
      const colour = details.colour ? `(${details.colour})` : '';
      const year = details.yearOfManufacture ? `${details.yearOfManufacture}` : '';
      const fuelType = details.fuelType || '';
      const engineCapacity = details.engineCapacity ? `${details.engineCapacity}cc` : '';
      const co2 = details.co2Emissions ? `${details.co2Emissions}g/km CO₂` : '';

      const mot = details.motStatus ? `MOT: ${details.motStatus}${details.motExpiryDate ? ` (exp ${formatMaybeDate(details.motExpiryDate)})` : ''}` : null;
      const tax = details.taxStatus ? `Tax: ${details.taxStatus}${details.taxDueDate ? ` (due ${formatMaybeDate(details.taxDueDate)})` : ''}` : null;
      const exportWarning = details.markedForExport ? '⚠️ Marked for export' : null;

      const specs = [year, fuelType, engineCapacity, co2].filter(Boolean).join(' • ');
      const headline = makeModel ? `✅ DVLA: ${makeModel} ${colour}`.trim() : '✅ DVLA match found';

      setLookupSuccess([headline, specs, mot, tax, exportWarning].filter(Boolean).join(' • '));
    } catch (e) {
      setLookupError(getErrorMessage(e));
    } finally {
      setLookupLoading(false);
    }
  };

  const validate = (): boolean => {
    setFieldErrors({});
    setSubmitError(null);

    // Prepare data for validation
    const dataToValidate = {
      pcn_number: form.pcn_number,
      vrm: form.vrn,
      issue_date: form.issue_date,
      contravention_date: form.contravention_date,
      amount: Number(form.amount),
      location: form.location,
      authority: form.issuing_authority,
      stage: form.stage,
      status: 'pending_review' as const,
    };

    // Run Zod validation
    const result = validateTicketForm(dataToValidate);

    if (!result.success) {
      // Set field-level errors
      setFieldErrors(result.errors);

      // Also set the first error as the main submit error
      const firstError = Object.values(result.errors)[0];
      setSubmitError(firstError || 'Please fix validation errors');

      logger.warn('Ticket form validation failed', { errors: result.errors, form });
      return false;
    }

    // Additional business logic validations not in Zod schema
    const amount = Number(form.amount);
    const discount = form.discount_amount.trim() ? Number(form.discount_amount) : null;

    if (discount !== null && discount > amount) {
      setFieldErrors({ discount_amount: 'Discount cannot exceed amount' });
      setSubmitError('Discount cannot exceed amount');
      return false;
    }

    const issue = new Date(form.issue_date);
    const contra = new Date(form.contravention_date);
    if (issue.getTime() && contra.getTime() && contra.getTime() > issue.getTime()) {
      setFieldErrors({ contravention_date: 'Contravention date should not be after issue date' });
      setSubmitError('Contravention date should not be after issue date');
      return false;
    }

    logger.info('Ticket form validation passed', { form });
    return true;
  };

  const handleSubmit = async () => {
    // Run validation
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      logger.info('Creating manual ticket', { pcn_number: form.pcn_number, vrm: vrmNormalized });

      const result = await ApiService.createTicket({
        pcn_number: form.pcn_number.trim(),
        vrn: vrmNormalized,
        issue_date: toIsoDateTime(form.issue_date),
        contravention_date: toIsoDateTime(form.contravention_date),
        location: form.location.trim(),
        issuing_authority: form.issuing_authority.trim(),
        amount: Number(form.amount),
        discount_amount: form.discount_amount.trim() ? Number(form.discount_amount) : undefined,
        stage: form.stage,
        contravention_type: form.contravention_type
      });

      logger.info('Manual ticket created successfully', { ticketId: result.ticket.id, action: result.action });

      if (result.action === 'created') {
        try {
          const trialResult = await ApiService.incrementFleetTrialProcessed(1);
          if (trialResult?.expired_by === 'pcn_limit') {
            setLookupSuccess('Manual PCN saved. Your 3-PCN trial snapshot is ready.');
          } else if (trialResult?.expired_by === 'time') {
            setLookupSuccess('Manual PCN saved. The 7-day trial has ended, so new processing is paused until a plan is selected.');
          }
        } catch (trialErr) {
          logger.warn('Manual ticket saved but trial counter did not update', { trialErr });
        }
      }

      setCreatedTicket(result.ticket);
      setCreateAction(result.action);
      onCreated(result.ticket);
    } catch (e) {
      const errorMsg = getErrorMessage(e);
      logger.error('Failed to create manual ticket', e, { form });
      setSubmitError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      pcn_number: '',
      vrn: '',
      issue_date: '',
      contravention_date: '',
      location: '',
      issuing_authority: '',
      amount: '',
      discount_amount: '',
      stage: 'Notice to Owner',
      contravention_type: 'Parking Violation'
    });
    setVehicleDetails({});
    setLookupError(null);
    setSubmitError(null);
    setFieldErrors({});
    setCreatedTicket(null);
    setCreateAction(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Manual Ticket" maxWidth="max-w-2xl">
      <div className="p-6 space-y-6">
        {createdTicket ? (
          <div className="space-y-5">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5" />
              <div className="min-w-0">
                <div className="font-bold text-green-900 dark:text-green-100">
                  {createAction === 'merged' ? 'Ticket updated' : 'Ticket created'}
                </div>
                <div className="text-sm text-green-800 dark:text-green-200 mt-1">
                  {createdTicket.pcn_number} • {createdTicket.vrn} • £{createdTicket.amount}
                </div>
                <div className="text-xs text-green-800/80 dark:text-green-200/80 mt-1 truncate">
                  {createdTicket.issuing_authority} • {createdTicket.location}
                </div>
                {createAction === 'merged' && (
                  <div className="text-xs text-green-800/80 dark:text-green-200/80 mt-1">
                    Duplicate PCN detected — merged into the existing ticket and updated the stage if needed.
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              {onViewTicket && (
                <button
                  onClick={() => onViewTicket(createdTicket.id)}
                  className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
                >
                  View Ticket
                </button>
              )}
              <button
                onClick={handleClose}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            {submitError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                {submitError}
              </div>
            )}

            {/* Core fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  PCN Number {fieldErrors.pcn_number && <span className="text-red-600 dark:text-red-400">*</span>}
                </label>
                <input
                  value={form.pcn_number}
                  onChange={(e) => setField('pcn_number', e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    fieldErrors.pcn_number
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-700 focus:ring-primary-500'
                  } bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2`}
                  placeholder="e.g. AF0904144A"
                />
                {fieldErrors.pcn_number && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>{fieldErrors.pcn_number}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  VRM {fieldErrors.vrm && <span className="text-red-600 dark:text-red-400">*</span>}
                </label>
                <div className="relative">
                  <input
                    value={form.vrn}
                    onChange={(e) => setField('vrn', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      fieldErrors.vrm
                        ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                        : 'border-slate-300 dark:border-slate-700 focus:ring-primary-500'
                    } bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 pr-10 font-mono uppercase`}
                    placeholder="e.g. YY67TYG"
                  />
                  <button
                    type="button"
                    onClick={() => setField('vrn', '')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    title="Clear"
                  >
                    <X size={16} />
                  </button>
                </div>
                {fieldErrors.vrm && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>{fieldErrors.vrm}</span>
                  </div>
                )}

                {/* Suggestions */}
                {vrmNormalized && matchingVehicles.length > 0 && (
                  <div className="mt-2 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
                    {matchingVehicles.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => applyVehicleSuggestion(v)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between"
                      >
                        <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{v.vrm}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 truncate ml-3">
                          {[v.make, v.model, v.colour].filter(Boolean).join(' • ') || 'Saved vehicle'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {loadingVehicles && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Loading vehicles…</div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  Issue Date {fieldErrors.issue_date && <span className="text-red-600 dark:text-red-400">*</span>}
                </label>
                <input
                  type="date"
                  value={form.issue_date}
                  onChange={(e) => setField('issue_date', e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    fieldErrors.issue_date
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-700 focus:ring-primary-500'
                  } bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2`}
                />
                {fieldErrors.issue_date && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>{fieldErrors.issue_date}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  Contravention Date {fieldErrors.contravention_date && <span className="text-red-600 dark:text-red-400">*</span>}
                </label>
                <input
                  type="date"
                  value={form.contravention_date}
                  onChange={(e) => setField('contravention_date', e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    fieldErrors.contravention_date
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-700 focus:ring-primary-500'
                  } bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2`}
                />
                {fieldErrors.contravention_date && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>{fieldErrors.contravention_date}</span>
                  </div>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  Location {fieldErrors.location && <span className="text-red-600 dark:text-red-400">*</span>}
                </label>
                <input
                  value={form.location}
                  onChange={(e) => setField('location', e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    fieldErrors.location
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-700 focus:ring-primary-500'
                  } bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2`}
                  placeholder="e.g. Pevensey Road (Eastbourne)"
                />
                {fieldErrors.location && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>{fieldErrors.location}</span>
                  </div>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  Issuing Authority {fieldErrors.authority && <span className="text-red-600 dark:text-red-400">*</span>}
                </label>
                <input
                  value={form.issuing_authority}
                  onChange={(e) => setField('issuing_authority', e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    fieldErrors.authority
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-700 focus:ring-primary-500'
                  } bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2`}
                  placeholder="e.g. London Borough of Redbridge"
                />
                {fieldErrors.authority && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>{fieldErrors.authority}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Amounts + classification */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  Amount (£) {fieldErrors.amount && <span className="text-red-600 dark:text-red-400">*</span>}
                </label>
                <input
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(e) => setField('amount', e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    fieldErrors.amount
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-700 focus:ring-primary-500'
                  } bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2`}
                  placeholder="e.g. 115"
                />
                {fieldErrors.amount && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>{fieldErrors.amount}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  Discount (£) {fieldErrors.discount_amount && <span className="text-red-600 dark:text-red-400">*</span>}
                </label>
                <input
                  inputMode="decimal"
                  value={form.discount_amount}
                  onChange={(e) => setField('discount_amount', e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    fieldErrors.discount_amount
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-700 focus:ring-primary-500'
                  } bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2`}
                  placeholder="Optional"
                />
                {fieldErrors.discount_amount && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>{fieldErrors.discount_amount}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Stage</label>
                <select
                  value={form.stage}
                  onChange={(e) => setField('stage', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <optgroup label="Intake (Fleet Mode)">
                    <option value="Notice to Owner">Notice to Owner (NtO)</option>
                    <option value="PCN Issued">Postal PCN / PCN (rare)</option>
                  </optgroup>
                  <optgroup label="Private Parking">
                    <option value="Parking Charge Notice">Parking Charge Notice (PCN)</option>
                    <option value="Notice to Keeper">Notice to Keeper (NtK)</option>
                    <option value="Notice to Driver">Notice to Driver (NtD)</option>
                    <option value="Reminder">Reminder</option>
                    <option value="Final Reminder">Final Reminder</option>
                    <option value="Debt Recovery">Debt Recovery</option>
                    <option value="Letter Before Claim">Letter Before Claim</option>
                    <option value="Court Claim">Court Claim</option>
                  </optgroup>
                  <option value="Charge Certificate">Charge Certificate</option>
                  <option value="Order for Recovery">Order for Recovery</option>
                </select>
              </div>
              <div className="sm:col-span-3">
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Classification</label>
                <select
                  value={form.contravention_type}
                  onChange={(e) => setField('contravention_type', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* DVLA lookup */}
            <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-bold text-slate-800 dark:text-white">Vehicle details (DVLA)</div>
                <button
                  type="button"
                  onClick={runDvlaLookup}
                  disabled={lookupLoading}
                  className={`px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors ${
                    hasDvlaLookup
                      ? 'bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-100'
                      : 'bg-white/60 dark:bg-slate-900/40 border border-slate-300 dark:border-slate-700 text-slate-400 cursor-not-allowed'
                  }`}
                  title={hasDvlaLookup ? 'Lookup VRM' : 'Upgrade to Pro to enable DVLA lookup'}
                >
                  {lookupLoading ? <Loader2 size={16} className="animate-spin" /> : hasDvlaLookup ? <Search size={16} /> : <Lock size={16} />}
                  {hasDvlaLookup ? 'Lookup VRM' : 'DVLA Lookup'}
                </button>
              </div>

              {lookupError && <div className="text-sm text-red-600 dark:text-red-400">{lookupError}</div>}
              {lookupSuccess && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-800 dark:text-green-200 flex items-start justify-between gap-3">
                  <div className="min-w-0">{lookupSuccess}</div>
                  <button
                    type="button"
                    onClick={() => setLookupSuccess(null)}
                    className="text-green-700/70 hover:text-green-800 dark:text-green-200/70 dark:hover:text-green-200 shrink-0"
                    aria-label="Dismiss"
                    title="Dismiss"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Make</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">{vehicleDetails.make || '-'}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Model</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">{vehicleDetails.model || '-'}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Colour</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">{vehicleDetails.colour || '-'}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">MOT</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">{vehicleDetails.motStatus || '-'}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Expiry: {formatMaybeDate(vehicleDetails.motExpiryDate)}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Tax</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">{vehicleDetails.taxStatus || '-'}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Due: {formatMaybeDate(vehicleDetails.taxDueDate)}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">First registered</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">{vehicleDetails.monthOfFirstRegistration || '-'}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    V5C: {formatMaybeDate(vehicleDetails.dateOfLastV5CIssued)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Fuel / Euro</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">
                    {vehicleDetails.fuelType || '-'}{vehicleDetails.euroStatus ? ` • ${vehicleDetails.euroStatus}` : ''}
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Engine</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">
                    {vehicleDetails.engineCapacity ? `${vehicleDetails.engineCapacity}cc` : '-'}
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">CO₂</div>
                  <div className="font-medium text-slate-800 dark:text-slate-200">
                    {typeof vehicleDetails.co2Emissions === 'number' ? `${vehicleDetails.co2Emissions} g/km` : '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors flex items-center gap-2 disabled:opacity-60"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Create Ticket
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
